/**
 * Occasion names in the Indian languages the UI ships (hi, bn, ml, ta, te,
 * kn, ur), by curated occasion id. OCCASION_DATASET keeps English names as
 * its data; the calendar, the plan and the catalogue show the name in the
 * reader's language through `localizedOccasionName`, and fall back to the
 * English name for any language or occasion not listed here.
 *
 * Covers what an Indian workspace sees: the IN pack and the global moments
 * shown everywhere. Festival names use the form the language itself uses
 * (Onam, Vinayaka Chaturthi, Bakrid...), not a transliteration of English.
 * Industry days are not translated yet.
 */
export const OCCASION_NAME_LANGUAGES = ['hi', 'bn', 'ml', 'ta', 'te', 'kn', 'ur'] as const;
export type OccasionNameLanguage = (typeof OCCASION_NAME_LANGUAGES)[number];

export const OCCASION_NAMES: Readonly<Record<OccasionNameLanguage, Readonly<Record<string, string>>>> = {
  hi: {
    'in-easter': 'ईस्टर',
    'in-school-reopening': 'स्कूल खुलने का समय',
    'global-new-years-day': 'नववर्ष दिवस',
    'global-january-sales': 'जनवरी सेल',
    'global-valentines-day': 'वैलेंटाइन डे',
    'global-womens-day': 'अंतरराष्ट्रीय महिला दिवस',
    'global-april-fools': 'अप्रैल फूल दिवस',
    'global-easter-sunday': 'ईस्टर संडे',
    'global-earth-day': 'पृथ्वी दिवस',
    'global-mothers-day-second-sunday-may': 'मदर्स डे',
    'global-fathers-day-third-sunday-june': 'फादर्स डे',
    'global-pride-month': 'प्राइड मंथ',
    'global-summer-sale': 'समर सेल की शुरुआत',
    'global-back-to-school': 'स्कूल खुलने का समय',
    'global-world-animal-day': 'विश्व पशु दिवस',
    'global-halloween': 'हैलोवीन',
    'global-singles-day': 'सिंगल्स डे',
    'global-black-friday': 'ब्लैक फ्राइडे',
    'global-cyber-monday': 'साइबर मंडे',
    'global-giving-tuesday': 'गिविंग ट्यूज़डे',
    'global-christmas-eve': 'क्रिसमस की पूर्व संध्या',
    'global-christmas-day': 'क्रिसमस',
    'global-new-years-eve': 'नववर्ष की पूर्व संध्या',
    'global-ramadan-start': 'रमज़ान की शुरुआत',
    'global-ramadan-start-2': 'रमज़ान की शुरुआत',
    'global-eid-al-fitr': 'ईद-उल-फ़ित्र',
    'global-eid-al-adha': 'ईद-उल-अज़हा (बकरीद)',
    'global-chinese-new-year': 'चीनी नववर्ष',
    'in-makar-sankranti': 'मकर संक्रांति',
    'in-pongal': 'पोंगल',
    'in-vasant-panchami': 'वसंत पंचमी',
    'in-republic-day': 'गणतंत्र दिवस',
    'in-maha-shivaratri': 'महाशिवरात्रि',
    'in-holi': 'होली',
    'in-ugadi': 'उगादि',
    'in-gudi-padwa': 'गुड़ी पड़वा',
    'in-ram-navami': 'राम नवमी',
    'in-baisakhi': 'बैसाखी',
    'in-rongali-bihu': 'रोंगाली बिहू',
    'in-ambedkar-jayanti': 'आंबेडकर जयंती',
    'in-independence-day': 'स्वतंत्रता दिवस',
    'in-friendship-day': 'फ्रेंडशिप डे',
    'in-onam': 'ओणम (तिरुवोणम)',
    'in-raksha-bandhan': 'रक्षा बंधन',
    'in-janmashtami': 'कृष्ण जन्माष्टमी',
    'in-teachers-day': 'शिक्षक दिवस',
    'in-ganesh-chaturthi': 'गणेश चतुर्थी',
    'in-gandhi-jayanti': 'गांधी जयंती',
    'in-navratri': 'नवरात्रि',
    'in-durga-puja': 'दुर्गा पूजा',
    'in-dussehra': 'दशहरा (विजयादशमी)',
    'in-karva-chauth': 'करवा चौथ',
    'in-dhanteras': 'धनतेरस',
    'in-diwali': 'दीवाली',
    'in-bhai-dooj': 'भाई दूज',
    'in-childrens-day': 'बाल दिवस',
    'in-chhath-puja': 'छठ पूजा',
    'in-guru-nanak-jayanti': 'गुरु नानक जयंती',
    'in-lohri': 'लोहड़ी',
    'in-puthandu': 'पुथांडु (तमिल नववर्ष)',
    'in-vishu': 'विशु',
    'in-poila-boishakh': 'पोइला बोइशाख (बंगाली नववर्ष)',
    'in-good-friday': 'गुड फ्राइडे',
    'in-akshaya-tritiya': 'अक्षय तृतीया',
    'in-muharram': 'मुहर्रम',
    'in-chingam-1': 'चिंगम 1 (मलयालम नववर्ष)',
    'in-milad-un-nabi': 'मिलाद-उन-नबी',
    'in-kerala-piravi': 'केरल पिरवी (केरल स्थापना दिवस)',
  },
  bn: {
    'in-easter': 'ইস্টার',
    'in-school-reopening': 'স্কুল খোলার সময়',
    'global-new-years-day': 'ইংরেজি নববর্ষ',
    'global-january-sales': 'জানুয়ারি সেল',
    'global-valentines-day': 'ভ্যালেন্টাইনস ডে',
    'global-womens-day': 'আন্তর্জাতিক নারী দিবস',
    'global-april-fools': 'এপ্রিল ফুলস ডে',
    'global-easter-sunday': 'ইস্টার সানডে',
    'global-earth-day': 'বিশ্ব ধরিত্রী দিবস',
    'global-mothers-day-second-sunday-may': 'মাতৃ দিবস',
    'global-fathers-day-third-sunday-june': 'পিতৃ দিবস',
    'global-pride-month': 'প্রাইড মাস',
    'global-summer-sale': 'সামার সেল শুরু',
    'global-back-to-school': 'স্কুল খোলার সময়',
    'global-world-animal-day': 'বিশ্ব প্রাণী দিবস',
    'global-halloween': 'হ্যালোইন',
    'global-singles-day': 'সিঙ্গলস ডে',
    'global-black-friday': 'ব্ল্যাক ফ্রাইডে',
    'global-cyber-monday': 'সাইবার মানডে',
    'global-giving-tuesday': 'গিভিং টিউসডে',
    'global-christmas-eve': 'বড়দিনের আগের সন্ধ্যা',
    'global-christmas-day': 'বড়দিন',
    'global-new-years-eve': 'নববর্ষের আগের রাত',
    'global-ramadan-start': 'রমজান শুরু',
    'global-ramadan-start-2': 'রমজান শুরু',
    'global-eid-al-fitr': 'ঈদুল ফিতর',
    'global-eid-al-adha': 'ঈদুল আজহা',
    'global-chinese-new-year': 'চীনা নববর্ষ',
    'in-makar-sankranti': 'মকর সংক্রান্তি',
    'in-pongal': 'পোঙ্গল',
    'in-vasant-panchami': 'বসন্ত পঞ্চমী',
    'in-republic-day': 'প্রজাতন্ত্র দিবস',
    'in-maha-shivaratri': 'মহাশিবরাত্রি',
    'in-holi': 'হোলি',
    'in-ugadi': 'উগাদি',
    'in-gudi-padwa': 'গুড়ি পাড়ওয়া',
    'in-ram-navami': 'রাম নবমী',
    'in-baisakhi': 'বৈশাখী',
    'in-rongali-bihu': 'রঙ্গালি বিহু',
    'in-ambedkar-jayanti': 'আম্বেদকর জয়ন্তী',
    'in-independence-day': 'স্বাধীনতা দিবস',
    'in-friendship-day': 'বন্ধুত্ব দিবস',
    'in-onam': 'ওনাম (তিরুওনাম)',
    'in-raksha-bandhan': 'রাখি বন্ধন',
    'in-janmashtami': 'জন্মাষ্টমী',
    'in-teachers-day': 'শিক্ষক দিবস',
    'in-ganesh-chaturthi': 'গণেশ চতুর্থী',
    'in-gandhi-jayanti': 'গান্ধী জয়ন্তী',
    'in-navratri': 'নবরাত্রি',
    'in-durga-puja': 'দুর্গাপুজো',
    'in-dussehra': 'দশেরা (বিজয়া দশমী)',
    'in-karva-chauth': 'করবা চৌথ',
    'in-dhanteras': 'ধনতেরাস',
    'in-diwali': 'দীপাবলি',
    'in-bhai-dooj': 'ভাইফোঁটা',
    'in-childrens-day': 'শিশু দিবস',
    'in-chhath-puja': 'ছট পুজো',
    'in-guru-nanak-jayanti': 'গুরু নানক জয়ন্তী',
    'in-lohri': 'লোহরি',
    'in-puthandu': 'পুথান্ডু (তামিল নববর্ষ)',
    'in-vishu': 'বিশু',
    'in-poila-boishakh': 'পয়লা বৈশাখ',
    'in-good-friday': 'গুড ফ্রাইডে',
    'in-akshaya-tritiya': 'অক্ষয় তৃতীয়া',
    'in-muharram': 'মহরম',
    'in-chingam-1': 'চিঙ্গম ১ (মালয়ালম নববর্ষ)',
    'in-milad-un-nabi': 'মিলাদ-উন-নবী',
    'in-kerala-piravi': 'কেরল পিরাভি (কেরল দিবস)',
  },
  ml: {
    'in-easter': 'ഈസ്റ്റർ',
    'in-school-reopening': 'സ്കൂൾ തുറക്കൽ',
    'global-new-years-day': 'പുതുവത്സര ദിനം',
    'global-january-sales': 'ജനുവരി സെയിൽ',
    'global-valentines-day': 'വാലന്റൈൻസ് ഡേ',
    'global-womens-day': 'അന്താരാഷ്ട്ര വനിതാ ദിനം',
    'global-april-fools': 'ഏപ്രിൽ ഫൂൾ ദിനം',
    'global-easter-sunday': 'ഈസ്റ്റർ',
    'global-earth-day': 'ഭൗമ ദിനം',
    'global-mothers-day-second-sunday-may': 'മാതൃദിനം',
    'global-fathers-day-third-sunday-june': 'പിതൃദിനം',
    'global-pride-month': 'പ്രൈഡ് മാസം',
    'global-summer-sale': 'സമ്മർ സെയിൽ തുടങ്ങുന്നു',
    'global-back-to-school': 'സ്കൂൾ തുറക്കൽ',
    'global-world-animal-day': 'ലോക മൃഗ ദിനം',
    'global-halloween': 'ഹാലോവീൻ',
    'global-singles-day': 'സിംഗിൾസ് ഡേ',
    'global-black-friday': 'ബ്ലാക്ക് ഫ്രൈഡേ',
    'global-cyber-monday': 'സൈബർ മൺഡേ',
    'global-giving-tuesday': 'ഗിവിങ് ട്യൂസ്‌ഡേ',
    'global-christmas-eve': 'ക്രിസ്മസ് തലേന്ന്',
    'global-christmas-day': 'ക്രിസ്മസ്',
    'global-new-years-eve': 'പുതുവത്സരത്തലേന്ന്',
    'global-ramadan-start': 'റമദാൻ ആരംഭം',
    'global-ramadan-start-2': 'റമദാൻ ആരംഭം',
    'global-eid-al-fitr': 'ചെറിയ പെരുന്നാൾ (ഈദുൽ ഫിത്ർ)',
    'global-eid-al-adha': 'ബലി പെരുന്നാൾ (ഈദുൽ അദ്ഹ)',
    'global-chinese-new-year': 'ചൈനീസ് പുതുവത്സരം',
    'in-makar-sankranti': 'മകര സംക്രാന്തി',
    'in-pongal': 'പൊങ്കൽ',
    'in-vasant-panchami': 'വസന്ത പഞ്ചമി',
    'in-republic-day': 'റിപ്പബ്ലിക് ദിനം',
    'in-maha-shivaratri': 'മഹാശിവരാത്രി',
    'in-holi': 'ഹോളി',
    'in-ugadi': 'ഉഗാദി',
    'in-gudi-padwa': 'ഗുഡി പഡ്‌വ',
    'in-ram-navami': 'രാമനവമി',
    'in-baisakhi': 'ബൈസാഖി',
    'in-rongali-bihu': 'രൊംഗാലി ബിഹു',
    'in-ambedkar-jayanti': 'അംബേദ്കർ ജയന്തി',
    'in-independence-day': 'സ്വാതന്ത്ര്യ ദിനം',
    'in-friendship-day': 'സൗഹൃദ ദിനം',
    'in-onam': 'ഓണം (തിരുവോണം)',
    'in-raksha-bandhan': 'രക്ഷാബന്ധൻ',
    'in-janmashtami': 'ശ്രീകൃഷ്ണ ജയന്തി',
    'in-teachers-day': 'അധ്യാപക ദിനം',
    'in-ganesh-chaturthi': 'വിനായക ചതുർത്ഥി',
    'in-gandhi-jayanti': 'ഗാന്ധി ജയന്തി',
    'in-navratri': 'നവരാത്രി',
    'in-durga-puja': 'ദുർഗാ പൂജ',
    'in-dussehra': 'ദസറ (വിജയദശമി)',
    'in-karva-chauth': 'കർവാ ചൗത്ത്',
    'in-dhanteras': 'ധൻതേരസ്',
    'in-diwali': 'ദീപാവലി',
    'in-bhai-dooj': 'ഭായ് ദൂജ്',
    'in-childrens-day': 'ശിശുദിനം',
    'in-chhath-puja': 'ഛഠ് പൂജ',
    'in-guru-nanak-jayanti': 'ഗുരു നാനാക്ക് ജയന്തി',
    'in-lohri': 'ലോഹ്രി',
    'in-puthandu': 'പുത്താണ്ട് (തമിഴ് പുതുവർഷം)',
    'in-vishu': 'വിഷു',
    'in-poila-boishakh': 'പൊയില ബൈശാഖ് (ബംഗാളി പുതുവർഷം)',
    'in-good-friday': 'ദുഃഖവെള്ളി',
    'in-akshaya-tritiya': 'അക്ഷയ തൃതീയ',
    'in-muharram': 'മുഹറം',
    'in-chingam-1': 'ചിങ്ങം 1 (മലയാള പുതുവർഷം)',
    'in-milad-un-nabi': 'നബിദിനം',
    'in-kerala-piravi': 'കേരളപ്പിറവി',
  },
  ta: {
    'in-easter': 'ஈஸ்டர்',
    'in-school-reopening': 'பள்ளி திறப்பு',
    'global-new-years-day': 'ஆங்கிலப் புத்தாண்டு',
    'global-january-sales': 'ஜனவரி விற்பனை',
    'global-valentines-day': 'காதலர் தினம்',
    'global-womens-day': 'சர்வதேச மகளிர் தினம்',
    'global-april-fools': 'ஏப்ரல் முட்டாள்கள் தினம்',
    'global-easter-sunday': 'ஈஸ்டர் ஞாயிறு',
    'global-earth-day': 'புவி தினம்',
    'global-mothers-day-second-sunday-may': 'அன்னையர் தினம்',
    'global-fathers-day-third-sunday-june': 'தந்தையர் தினம்',
    'global-pride-month': 'பெருமை மாதம்',
    'global-summer-sale': 'கோடை விற்பனை தொடக்கம்',
    'global-back-to-school': 'பள்ளி திறப்பு',
    'global-world-animal-day': 'உலக விலங்குகள் தினம்',
    'global-halloween': 'ஹாலோவீன்',
    'global-singles-day': 'சிங்கிள்ஸ் டே',
    'global-black-friday': 'பிளாக் ஃப்ரைடே',
    'global-cyber-monday': 'சைபர் மண்டே',
    'global-giving-tuesday': 'கிவிங் டியூஸ்டே',
    'global-christmas-eve': 'கிறிஸ்துமஸ் முன்தினம்',
    'global-christmas-day': 'கிறிஸ்துமஸ்',
    'global-new-years-eve': 'புத்தாண்டு முன்தினம்',
    'global-ramadan-start': 'ரமலான் தொடக்கம்',
    'global-ramadan-start-2': 'ரமலான் தொடக்கம்',
    'global-eid-al-fitr': 'ரமலான் பெருநாள் (ஈதுல் பித்ர்)',
    'global-eid-al-adha': 'பக்ரீத் (ஈதுல் அழ்ஹா)',
    'global-chinese-new-year': 'சீனப் புத்தாண்டு',
    'in-makar-sankranti': 'மகர சங்கராந்தி',
    'in-pongal': 'பொங்கல்',
    'in-vasant-panchami': 'வசந்த பஞ்சமி',
    'in-republic-day': 'குடியரசு தினம்',
    'in-maha-shivaratri': 'மகா சிவராத்திரி',
    'in-holi': 'ஹோலி',
    'in-ugadi': 'யுகாதி',
    'in-gudi-padwa': 'குடி பட்வா',
    'in-ram-navami': 'ராம நவமி',
    'in-baisakhi': 'பைசாகி',
    'in-rongali-bihu': 'ரொங்காலி பிஹு',
    'in-ambedkar-jayanti': 'அம்பேத்கர் ஜெயந்தி',
    'in-independence-day': 'சுதந்திர தினம்',
    'in-friendship-day': 'நண்பர்கள் தினம்',
    'in-onam': 'ஓணம் (திருவோணம்)',
    'in-raksha-bandhan': 'ரக்ஷா பந்தன்',
    'in-janmashtami': 'கிருஷ்ண ஜெயந்தி',
    'in-teachers-day': 'ஆசிரியர் தினம்',
    'in-ganesh-chaturthi': 'விநாயகர் சதுர்த்தி',
    'in-gandhi-jayanti': 'காந்தி ஜெயந்தி',
    'in-navratri': 'நவராத்திரி',
    'in-durga-puja': 'துர்கா பூஜை',
    'in-dussehra': 'தசரா (விஜயதசமி)',
    'in-karva-chauth': 'கர்வா சௌத்',
    'in-dhanteras': 'தந்தேரஸ்',
    'in-diwali': 'தீபாவளி',
    'in-bhai-dooj': 'பாய் தூஜ்',
    'in-childrens-day': 'குழந்தைகள் தினம்',
    'in-chhath-puja': 'சாத் பூஜை',
    'in-guru-nanak-jayanti': 'குரு நானக் ஜெயந்தி',
    'in-lohri': 'லோரி',
    'in-puthandu': 'புத்தாண்டு (தமிழ்ப் புத்தாண்டு)',
    'in-vishu': 'விஷு',
    'in-poila-boishakh': 'பொய்லா பைசாக் (வங்காளப் புத்தாண்டு)',
    'in-good-friday': 'புனித வெள்ளி',
    'in-akshaya-tritiya': 'அட்சய திருதியை',
    'in-muharram': 'முஹர்ரம்',
    'in-chingam-1': 'சிங்கம் 1 (மலையாளப் புத்தாண்டு)',
    'in-milad-un-nabi': 'மீலாது நபி',
    'in-kerala-piravi': 'கேரளப் பிறவி',
  },
  te: {
    'in-easter': 'ఈస్టర్',
    'in-school-reopening': 'బడులు తెరిచే సమయం',
    'global-new-years-day': 'నూతన సంవత్సర దినం',
    'global-january-sales': 'జనవరి సేల్',
    'global-valentines-day': 'వాలెంటైన్స్ డే',
    'global-womens-day': 'అంతర్జాతీయ మహిళా దినోత్సవం',
    'global-april-fools': 'ఏప్రిల్ ఫూల్స్ డే',
    'global-easter-sunday': 'ఈస్టర్ ఆదివారం',
    'global-earth-day': 'ధరిత్రీ దినోత్సవం',
    'global-mothers-day-second-sunday-may': 'మాతృ దినోత్సవం',
    'global-fathers-day-third-sunday-june': 'పితృ దినోత్సవం',
    'global-pride-month': 'ప్రైడ్ మాసం',
    'global-summer-sale': 'సమ్మర్ సేల్ ప్రారంభం',
    'global-back-to-school': 'బడులు తెరిచే సమయం',
    'global-world-animal-day': 'ప్రపంచ జంతు దినోత్సవం',
    'global-halloween': 'హాలోవీన్',
    'global-singles-day': 'సింగిల్స్ డే',
    'global-black-friday': 'బ్లాక్ ఫ్రైడే',
    'global-cyber-monday': 'సైబర్ మండే',
    'global-giving-tuesday': 'గివింగ్ ట్యూస్‌డే',
    'global-christmas-eve': 'క్రిస్మస్ ముందు రోజు',
    'global-christmas-day': 'క్రిస్మస్',
    'global-new-years-eve': 'నూతన సంవత్సర ముందు రోజు',
    'global-ramadan-start': 'రంజాన్ మాసం ప్రారంభం',
    'global-ramadan-start-2': 'రంజాన్ మాసం ప్రారంభం',
    'global-eid-al-fitr': 'రంజాన్ (ఈద్-ఉల్-ఫితర్)',
    'global-eid-al-adha': 'బక్రీద్ (ఈద్-ఉల్-అధా)',
    'global-chinese-new-year': 'చైనీస్ నూతన సంవత్సరం',
    'in-makar-sankranti': 'మకర సంక్రాంతి',
    'in-pongal': 'పొంగల్',
    'in-vasant-panchami': 'వసంత పంచమి',
    'in-republic-day': 'గణతంత్ర దినోత్సవం',
    'in-maha-shivaratri': 'మహా శివరాత్రి',
    'in-holi': 'హోళీ',
    'in-ugadi': 'ఉగాది',
    'in-gudi-padwa': 'గుడి పడ్వా',
    'in-ram-navami': 'శ్రీరామ నవమి',
    'in-baisakhi': 'బైసాఖీ',
    'in-rongali-bihu': 'రొంగాలి బిహు',
    'in-ambedkar-jayanti': 'అంబేడ్కర్ జయంతి',
    'in-independence-day': 'స్వాతంత్ర్య దినోత్సవం',
    'in-friendship-day': 'స్నేహితుల దినోత్సవం',
    'in-onam': 'ఓణం (తిరువోణం)',
    'in-raksha-bandhan': 'రక్షా బంధన్',
    'in-janmashtami': 'శ్రీకృష్ణ జన్మాష్టమి',
    'in-teachers-day': 'ఉపాధ్యాయ దినోత్సవం',
    'in-ganesh-chaturthi': 'వినాయక చవితి',
    'in-gandhi-jayanti': 'గాంధీ జయంతి',
    'in-navratri': 'నవరాత్రి',
    'in-durga-puja': 'దుర్గా పూజ',
    'in-dussehra': 'దసరా (విజయదశమి)',
    'in-karva-chauth': 'కర్వా చౌత్',
    'in-dhanteras': 'ధన్‌తేరస్',
    'in-diwali': 'దీపావళి',
    'in-bhai-dooj': 'భాయ్ దూజ్',
    'in-childrens-day': 'బాలల దినోత్సవం',
    'in-chhath-puja': 'ఛఠ్ పూజ',
    'in-guru-nanak-jayanti': 'గురు నానక్ జయంతి',
    'in-lohri': 'లోహ్రీ',
    'in-puthandu': 'పుత్తాండు (తమిళ కొత్త సంవత్సరం)',
    'in-vishu': 'విషు',
    'in-poila-boishakh': 'పొయిలా బైశాఖ్ (బెంగాలీ కొత్త సంవత్సరం)',
    'in-good-friday': 'గుడ్ ఫ్రైడే',
    'in-akshaya-tritiya': 'అక్షయ తృతీయ',
    'in-muharram': 'మొహర్రం',
    'in-chingam-1': 'చింగం 1 (మలయాళ కొత్త సంవత్సరం)',
    'in-milad-un-nabi': 'మిలాద్-ఉన్-నబీ',
    'in-kerala-piravi': 'కేరళ పిరవి',
  },
  kn: {
    'in-easter': 'ಈಸ್ಟರ್',
    'in-school-reopening': 'ಶಾಲೆ ಪುನರಾರಂಭ',
    'global-new-years-day': 'ಹೊಸ ವರ್ಷದ ದಿನ',
    'global-january-sales': 'ಜನವರಿ ಸೇಲ್',
    'global-valentines-day': 'ವ್ಯಾಲೆಂಟೈನ್ಸ್ ಡೇ',
    'global-womens-day': 'ಅಂತರರಾಷ್ಟ್ರೀಯ ಮಹಿಳಾ ದಿನ',
    'global-april-fools': 'ಏಪ್ರಿಲ್ ಫೂಲ್ಸ್ ಡೇ',
    'global-easter-sunday': 'ಈಸ್ಟರ್ ಭಾನುವಾರ',
    'global-earth-day': 'ಭೂ ದಿನ',
    'global-mothers-day-second-sunday-may': 'ತಾಯಂದಿರ ದಿನ',
    'global-fathers-day-third-sunday-june': 'ತಂದೆಯರ ದಿನ',
    'global-pride-month': 'ಪ್ರೈಡ್ ತಿಂಗಳು',
    'global-summer-sale': 'ಸಮ್ಮರ್ ಸೇಲ್ ಆರಂಭ',
    'global-back-to-school': 'ಶಾಲೆ ಪುನರಾರಂಭ',
    'global-world-animal-day': 'ವಿಶ್ವ ಪ್ರಾಣಿ ದಿನ',
    'global-halloween': 'ಹ್ಯಾಲೋವೀನ್',
    'global-singles-day': 'ಸಿಂಗಲ್ಸ್ ಡೇ',
    'global-black-friday': 'ಬ್ಲ್ಯಾಕ್ ಫ್ರೈಡೇ',
    'global-cyber-monday': 'ಸೈಬರ್ ಮಂಡೇ',
    'global-giving-tuesday': 'ಗಿವಿಂಗ್ ಟ್ಯೂಸ್‌ಡೇ',
    'global-christmas-eve': 'ಕ್ರಿಸ್ಮಸ್ ಹಿಂದಿನ ದಿನ',
    'global-christmas-day': 'ಕ್ರಿಸ್ಮಸ್',
    'global-new-years-eve': 'ಹೊಸ ವರ್ಷದ ಮುನ್ನಾದಿನ',
    'global-ramadan-start': 'ರಂಜಾನ್ ಆರಂಭ',
    'global-ramadan-start-2': 'ರಂಜಾನ್ ಆರಂಭ',
    'global-eid-al-fitr': 'ರಂಜಾನ್ (ಈದ್ ಉಲ್ ಫಿತ್ರ್)',
    'global-eid-al-adha': 'ಬಕ್ರೀದ್ (ಈದ್ ಉಲ್ ಅದ್ಹಾ)',
    'global-chinese-new-year': 'ಚೀನೀ ಹೊಸ ವರ್ಷ',
    'in-makar-sankranti': 'ಮಕರ ಸಂಕ್ರಾಂತಿ',
    'in-pongal': 'ಪೊಂಗಲ್',
    'in-vasant-panchami': 'ವಸಂತ ಪಂಚಮಿ',
    'in-republic-day': 'ಗಣರಾಜ್ಯೋತ್ಸವ',
    'in-maha-shivaratri': 'ಮಹಾ ಶಿವರಾತ್ರಿ',
    'in-holi': 'ಹೋಳಿ',
    'in-ugadi': 'ಯುಗಾದಿ',
    'in-gudi-padwa': 'ಗುಡಿ ಪಾಡ್ವಾ',
    'in-ram-navami': 'ರಾಮ ನವಮಿ',
    'in-baisakhi': 'ಬೈಸಾಖಿ',
    'in-rongali-bihu': 'ರೊಂಗಾಲಿ ಬಿಹು',
    'in-ambedkar-jayanti': 'ಅಂಬೇಡ್ಕರ್ ಜಯಂತಿ',
    'in-independence-day': 'ಸ್ವಾತಂತ್ರ್ಯ ದಿನಾಚರಣೆ',
    'in-friendship-day': 'ಸ್ನೇಹಿತರ ದಿನ',
    'in-onam': 'ಓಣಂ (ತಿರುವೋಣಂ)',
    'in-raksha-bandhan': 'ರಕ್ಷಾ ಬಂಧನ',
    'in-janmashtami': 'ಕೃಷ್ಣ ಜನ್ಮಾಷ್ಟಮಿ',
    'in-teachers-day': 'ಶಿಕ್ಷಕರ ದಿನಾಚರಣೆ',
    'in-ganesh-chaturthi': 'ಗಣೇಶ ಚತುರ್ಥಿ',
    'in-gandhi-jayanti': 'ಗಾಂಧಿ ಜಯಂತಿ',
    'in-navratri': 'ನವರಾತ್ರಿ',
    'in-durga-puja': 'ದುರ್ಗಾ ಪೂಜೆ',
    'in-dussehra': 'ದಸರಾ (ವಿಜಯದಶಮಿ)',
    'in-karva-chauth': 'ಕರ್ವಾ ಚೌತ್',
    'in-dhanteras': 'ಧನ್‌ತೇರಸ್',
    'in-diwali': 'ದೀಪಾವಳಿ',
    'in-bhai-dooj': 'ಭಾಯಿ ದೂಜ್',
    'in-childrens-day': 'ಮಕ್ಕಳ ದಿನಾಚರಣೆ',
    'in-chhath-puja': 'ಛಠ್ ಪೂಜೆ',
    'in-guru-nanak-jayanti': 'ಗುರು ನಾನಕ್ ಜಯಂತಿ',
    'in-lohri': 'ಲೋಹ್ರಿ',
    'in-puthandu': 'ಪುತ್ತಾಂಡು (ತಮಿಳು ಹೊಸ ವರ್ಷ)',
    'in-vishu': 'ವಿಷು',
    'in-poila-boishakh': 'ಪೊಯಿಲಾ ಬೈಶಾಖ್ (ಬಂಗಾಳಿ ಹೊಸ ವರ್ಷ)',
    'in-good-friday': 'ಗುಡ್ ಫ್ರೈಡೇ',
    'in-akshaya-tritiya': 'ಅಕ್ಷಯ ತೃತೀಯ',
    'in-muharram': 'ಮೊಹರಂ',
    'in-chingam-1': 'ಚಿಂಗಂ 1 (ಮಲಯಾಳಂ ಹೊಸ ವರ್ಷ)',
    'in-milad-un-nabi': 'ಈದ್ ಮಿಲಾದ್',
    'in-kerala-piravi': 'ಕೇರಳ ಪಿರವಿ',
  },
  ur: {
    'in-easter': 'ایسٹر',
    'in-school-reopening': 'اسکول کھلنے کا وقت',
    'global-new-years-day': 'نئے سال کا دن',
    'global-january-sales': 'جنوری سیل',
    'global-valentines-day': 'ویلنٹائن ڈے',
    'global-womens-day': 'خواتین کا عالمی دن',
    'global-april-fools': 'اپریل فول',
    'global-easter-sunday': 'ایسٹر',
    'global-earth-day': 'یومِ ارض',
    'global-mothers-day-second-sunday-may': 'مدرز ڈے',
    'global-fathers-day-third-sunday-june': 'فادرز ڈے',
    'global-pride-month': 'پرائیڈ مہینہ',
    'global-summer-sale': 'سمر سیل کا آغاز',
    'global-back-to-school': 'اسکول کھلنے کا وقت',
    'global-world-animal-day': 'جانوروں کا عالمی دن',
    'global-halloween': 'ہالووین',
    'global-singles-day': 'سنگلز ڈے',
    'global-black-friday': 'بلیک فرائیڈے',
    'global-cyber-monday': 'سائبر منڈے',
    'global-giving-tuesday': 'گیونگ ٹیوز ڈے',
    'global-christmas-eve': 'کرسمس کی شام',
    'global-christmas-day': 'کرسمس',
    'global-new-years-eve': 'نئے سال کی شام',
    'global-ramadan-start': 'رمضان کا آغاز',
    'global-ramadan-start-2': 'رمضان کا آغاز',
    'global-eid-al-fitr': 'عید الفطر',
    'global-eid-al-adha': 'عید الاضحیٰ',
    'global-chinese-new-year': 'چینی نیا سال',
    'in-makar-sankranti': 'مکر سنکرانتی',
    'in-pongal': 'پونگل',
    'in-vasant-panchami': 'وسنت پنچمی',
    'in-republic-day': 'یومِ جمہوریہ',
    'in-maha-shivaratri': 'مہا شیوراتری',
    'in-holi': 'ہولی',
    'in-ugadi': 'اُگادی',
    'in-gudi-padwa': 'گڑی پڑوا',
    'in-ram-navami': 'رام نومی',
    'in-baisakhi': 'بیساکھی',
    'in-rongali-bihu': 'رونگالی بیہو',
    'in-ambedkar-jayanti': 'امبیڈکر جینتی',
    'in-independence-day': 'یومِ آزادی',
    'in-friendship-day': 'فرینڈشپ ڈے',
    'in-onam': 'اونم (تروونم)',
    'in-raksha-bandhan': 'رکشا بندھن',
    'in-janmashtami': 'کرشن جنم اشٹمی',
    'in-teachers-day': 'یومِ اساتذہ',
    'in-ganesh-chaturthi': 'گنیش چترتھی',
    'in-gandhi-jayanti': 'گاندھی جینتی',
    'in-navratri': 'نوراتری',
    'in-durga-puja': 'درگا پوجا',
    'in-dussehra': 'دسہرہ (وجے دشمی)',
    'in-karva-chauth': 'کروا چوتھ',
    'in-dhanteras': 'دھن تیرس',
    'in-diwali': 'دیوالی',
    'in-bhai-dooj': 'بھائی دوج',
    'in-childrens-day': 'یومِ اطفال',
    'in-chhath-puja': 'چھٹھ پوجا',
    'in-guru-nanak-jayanti': 'گرو نانک جینتی',
    'in-lohri': 'لوہڑی',
    'in-puthandu': 'پتھانڈو (تمل نیا سال)',
    'in-vishu': 'وشو',
    'in-poila-boishakh': 'پوئلا بوئشاکھ (بنگالی نیا سال)',
    'in-good-friday': 'گڈ فرائیڈے',
    'in-akshaya-tritiya': 'اکشے ترتیا',
    'in-muharram': 'محرم',
    'in-chingam-1': 'چنگم 1 (ملیالم نیا سال)',
    'in-milad-un-nabi': 'میلاد النبی',
    'in-kerala-piravi': 'کیرالا پیروی',
  },
};

function isNameLanguage(language: string): language is OccasionNameLanguage {
  return (OCCASION_NAME_LANGUAGES as readonly string[]).includes(language);
}

/**
 * The occasion's name in `language` (a UI language tag, `ml` or `ml-IN`),
 * else `fallback`, its English name. Only curated ids are listed; a custom
 * occasion's name is the person's own and an imported holiday's comes from
 * its feed, so both pass through unchanged.
 */
export function localizedOccasionName(id: string, fallback: string, language: string): string {
  const base = (language || '').split('-')[0]?.toLowerCase() ?? '';
  if (!isNameLanguage(base)) return fallback;
  return OCCASION_NAMES[base][id] ?? fallback;
}
